import React from "react";
import { Link } from "react-router-dom";
import { Shield, Lock, Eye, Database, UserCheck, Mail } from "lucide-react";
import logo from "../assets/images/logo.png";
import PublicNavbar from "../components/PublicNavbar";

const Privacy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-950">
      {/* Navigation */}
      <PublicNavbar currentPage="/privacy" />

      {/* Header */}
      <section className="container mx-auto px-5 py-20">
        <div className="text-center max-w-4xl mx-auto">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            <Shield className="h-4 w-4 mr-2" />
            Privacy & Security
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-8">
            Privacy Policy
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-12">
            Last updated:{" "}
            {new Date().toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-20">
        <div className="container mx-auto px-5">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg dark:prose-invert max-w-none">
              {/* Introduction */}
              <div className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                  <Lock className="h-7 w-7 text-primary mr-3" />
                  1. Introduction
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Welcome to ObeliskRecovery. We are committed to protecting
                  your personal information and your right to privacy. If you
                  have any questions or concerns about this privacy policy, or
                  our practices with regards to your personal information,
                  please contact us at privacy@obeliskrecovery.com.
                </p>
                <p className="text-gray-600 dark:text-gray-400">
                  This Privacy Policy describes how we collect, use, store, and
                  share your personal information when you use our services,
                  which include our website, mobile applications, and related
                  services (collectively, the "Services").
                </p>
              </div>

              {/* Information We Collect */}
              <div className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                  <Database className="h-7 w-7 text-primary mr-3" />
                  2. Information We Collect
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  We collect personal information that you voluntarily provide
                  to us when you register for our Services, express an interest
                  in obtaining information about us or our products and
                  Services, or otherwise when you contact us.
                </p>

                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-6 mb-6">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                    Personal Information You Provide
                  </h3>
                  <ul className="space-y-3 text-gray-600 dark:text-gray-400">
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span>
                        Account Information: Name, email address, username, and
                        password
                      </span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span>
                        Wallet Information: Public wallet addresses (no private
                        keys)
                      </span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span>
                        Verification Documents: For KYC verification purposes
                      </span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span>
                        Transaction Data: Information related to your deposits,
                        withdrawals, and recovery requests
                      </span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span>
                        Communication Data: Messages, support tickets, and other
                        communications
                      </span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                    Information Automatically Collected
                  </h3>
                  <ul className="space-y-3 text-gray-600 dark:text-gray-400">
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span>
                        Device Information: IP address, browser type, device
                        type, operating system
                      </span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span>
                        Usage Data: Pages visited, features used, time spent on
                        the platform
                      </span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span>
                        Location Data: General location based on IP address for
                        security purposes
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* How We Use Information */}
              <div className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                  <Eye className="h-7 w-7 text-primary mr-3" />
                  3. How We Use Your Information
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  We use the information we collect for various business
                  purposes described below:
                </p>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700">
                    <h4 className="font-bold text-gray-900 dark:text-white mb-3">
                      Service Delivery
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      To provide and maintain our Services, process
                      transactions, and manage your account.
                    </p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700">
                    <h4 className="font-bold text-gray-900 dark:text-white mb-3">
                      Security & Protection
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      To protect against fraudulent activity, monitor for
                      security threats, and verify user identity.
                    </p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700">
                    <h4 className="font-bold text-gray-900 dark:text-white mb-3">
                      Communication
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      To send important updates, security alerts, and respond to
                      your inquiries.
                    </p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700">
                    <h4 className="font-bold text-gray-900 dark:text-white mb-3">
                      Legal Compliance
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      To comply with legal obligations, regulations, and law
                      enforcement requests.
                    </p>
                  </div>
                </div>
              </div>

              {/* Data Sharing */}
              <div className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                  <UserCheck className="h-7 w-7 text-primary mr-3" />
                  4. Data Sharing & Disclosure
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  We do not sell, rent, or trade your personal information to
                  third parties. We may share information only in the following
                  circumstances:
                </p>

                <div className="space-y-4">
                  <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <h4 className="font-bold text-gray-900 dark:text-white mb-2">
                      Service Providers
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      With trusted third-party providers who assist us in
                      operating our Services (e.g., hosting, payment processing,
                      customer support).
                    </p>
                  </div>
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <h4 className="font-bold text-gray-900 dark:text-white mb-2">
                      Legal Requirements
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      When required by law, regulation, or legal process, or to
                      protect our rights, property, or safety.
                    </p>
                  </div>
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <h4 className="font-bold text-gray-900 dark:text-white mb-2">
                      Business Transfers
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      In connection with a merger, acquisition, or sale of
                      assets, where your information may be transferred as part
                      of the transaction.
                    </p>
                  </div>
                </div>
              </div>

              {/* Data Security */}
              <div className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                  <Shield className="h-7 w-7 text-primary mr-3" />
                  5. Data Security
                </h2>
                <div className="space-y-4">
                  <p className="text-gray-600 dark:text-gray-400">
                    We implement appropriate technical and organizational
                    security measures designed to protect the security of any
                    personal information we process. However, no electronic
                    transmission over the Internet or information storage
                    technology can be guaranteed to be 100% secure.
                  </p>
                  <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 dark:text-white mb-4">
                      Our Security Measures Include:
                    </h4>
                    <ul className="space-y-2 text-gray-600 dark:text-gray-400">
                      <li className="flex items-center">
                        <div className="w-2 h-2 bg-primary rounded-full mr-3"></div>
                        End-to-end encryption for sensitive data
                      </li>
                      <li className="flex items-center">
                        <div className="w-2 h-2 bg-primary rounded-full mr-3"></div>
                        Regular security audits and penetration testing
                      </li>
                      <li className="flex items-center">
                        <div className="w-2 h-2 bg-primary rounded-full mr-3"></div>
                        Multi-factor authentication for account access
                      </li>
                      <li className="flex items-center">
                        <div className="w-2 h-2 bg-primary rounded-full mr-3"></div>
                        Secure data centers with physical security controls
                      </li>
                      <li className="flex items-center">
                        <div className="w-2 h-2 bg-primary rounded-full mr-3"></div>
                        Employee training on data protection and privacy
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Your Rights */}
              <div className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
                  6. Your Privacy Rights
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700">
                    <h4 className="font-bold text-gray-900 dark:text-white mb-3">
                      Access & Correction
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      You have the right to access and update your personal
                      information through your account settings.
                    </p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700">
                    <h4 className="font-bold text-gray-900 dark:text-white mb-3">
                      Data Portability
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      You can request a copy of your personal data in a
                      structured, machine-readable format.
                    </p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700">
                    <h4 className="font-bold text-gray-900 dark:text-white mb-3">
                      Deletion Rights
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      You may request deletion of your personal information,
                      subject to certain legal exceptions.
                    </p>
                  </div>
                  <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700">
                    <h4 className="font-bold text-gray-900 dark:text-white mb-3">
                      Opt-Out Rights
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      You can opt out of marketing communications at any time by
                      updating your preferences.
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                  <Mail className="h-7 w-7 text-primary mr-3" />
                  7. Contact Us
                </h2>
                <div className="bg-primary/5 rounded-xl p-8">
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    If you have questions or comments about this Privacy Policy,
                    you may contact our Data Protection Officer at:
                  </p>
                  <div className="space-y-2">
                    <p className="font-medium text-gray-900 dark:text-white">
                      Email: privacy@obeliskrecovery.com
                    </p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      Address: 123 Security Street, Crypto Valley, CV 10001
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-4">
                      We will respond to your request within 30 days.
                    </p>
                  </div>
                </div>
              </div>

              {/* Updates */}
              <div>
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
                  8. Updates to This Policy
                </h2>
                <p className="text-gray-600 dark:text-gray-400">
                  We may update this Privacy Policy from time to time. The
                  updated version will be indicated by an updated "Last updated"
                  date and the updated version will be effective as soon as it
                  is accessible. We encourage you to review this Privacy Policy
                  frequently to be informed of how we are protecting your
                  information.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="container mx-auto px-5">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img src={logo} alt="ObeliskRecovery" className="h-8 w-auto" />
                <span className="text-xl font-bold text-white">
                  ObeliskRecovery
                </span>
              </div>
              <p className="mb-4">
                Secure your crypto assets against market downturns and recover
                lost funds with expert assistance.
              </p>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Services</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/wallet" className="hover:text-white">
                    Wallet Protection
                  </Link>
                </li>
                <li>
                  <Link to="/recovery" className="hover:text-white">
                    Fund Recovery
                  </Link>
                </li>
                <li>
                  <Link to="/dashboard" className="hover:text-white">
                    Dashboard
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/privacy" className="hover:text-white">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="hover:text-white">
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Contact</h4>
              <p className="mb-2">privacy@obeliskrecovery.com</p>
              <p>24/7 Support Available</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center">
            <p>
              © {new Date().getFullYear()} ObeliskRecovery. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Privacy;
